// Puente Inquiapp ↔ Firebase.
// La app (public/index.html) usa la misma API que tenía como artifact de Claude:
//   window.claude.use('db' | 'user' | 'downloads' | 'sample')
// Este módulo inicia sesión con Firebase Auth y entrega esas APIs sobre Firestore.
import { initializeApp } from 'firebase/app';
import {
  getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  sendPasswordResetEmail, sendEmailVerification, signOut, reload, connectAuthEmulator,
} from 'firebase/auth';
import {
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
  doc, collection, getDoc, getDocs, setDoc, updateDoc, deleteDoc, onSnapshot,
  query, orderBy, limit, where, writeBatch, connectFirestoreEmulator,
} from 'firebase/firestore';
import { getAI, getGenerativeModel, GoogleAIBackend } from 'firebase/ai';
import { firebaseConfig, GEMINI_MODELOS } from './config.js';

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
auth.languageCode = 'es';
let fs;
try {
  fs = initializeFirestore(app, { ignoreUndefinedProperties: true, localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) });
} catch (e) {
  fs = initializeFirestore(app, { ignoreUndefinedProperties: true });
}
// Pruebas locales con los emuladores de Firebase: http://localhost:5000/?emu
if (/^(localhost|127\.0\.0\.1)$/.test(location.hostname) && new URLSearchParams(location.search).has('emu')) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(fs, '127.0.0.1', 8080);
}

/* ---------- escrituras: no bloquear la caja si se corta internet ---------- */
// Firestore guarda la escritura localmente al instante y la sincroniza al volver la conexión;
// la promesa sólo se resuelve cuando el servidor confirma. Si tarda, seguimos igual.
const ESPERA_MAX = 6000;
const escribir = p => {
  p.catch(e => { console.error('Escritura rechazada', e); avisar('No se pudo guardar un cambio: ' + (e?.code || e?.message || 'error')); });
  return Promise.race([p, new Promise(r => setTimeout(r, ESPERA_MAX))]);
};

/* ---------- API de base de datos compatible ---------- */
const snapDoc = s => ({ id: s.id, exists: s.exists(), data: () => s.data() });
const snapCol = qs => ({ docs: qs.docs.map(snapDoc), size: qs.size, empty: qs.empty });

function docApi(path) {
  const ref = doc(fs, path);
  return {
    id: ref.id, path,
    get: async () => snapDoc(await getDoc(ref)),
    set: (data, opts) => escribir(setDoc(ref, data, opts?.merge ? { merge: true } : {})),
    update: data => escribir(updateDoc(ref, data)),
    delete: () => escribir(deleteDoc(ref)),
    onSnapshot: (cb, err) => onSnapshot(ref, s => cb(snapDoc(s)), err),
    collection: sub => colApi(path + '/' + sub),
  };
}
function colApi(path, cons = []) {
  const base = collection(fs, path);
  const q = () => (cons.length ? query(base, ...cons) : base);
  return {
    id: base.id, path,
    doc: id => docApi(id ? path + '/' + id : doc(base).path),
    orderBy: (f, d) => colApi(path, [...cons, orderBy(f, d || 'asc')]),
    limit: n => colApi(path, [...cons, limit(n)]),
    where: (a, op, b) => colApi(path, [...cons, where(a, op, b)]),
    add: async data => { const ref = doc(base); await escribir(setDoc(ref, data)); return { id: ref.id }; },
    get: async () => snapCol(await getDocs(q())),
    onSnapshot: (cb, err) => onSnapshot(q(), s => cb(snapCol(s)), err),
  };
}
const dbApi = { doc: docApi, collection: colApi };

/* ---------- descargas ---------- */
const downloadsApi = {
  save: async ({ filename, data, mimeType }) => {
    const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType || (/\.xlsx$/i.test(filename) ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : /\.json$/i.test(filename) ? 'application/json' : 'application/octet-stream') });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: filename || 'archivo' });
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    return { ok: true };
  },
};

/* ---------- lectura de fotos y documentos con Gemini (Firebase AI Logic) ---------- */
let ai = null;
const modelo = nombre => {
  ai = ai || getAI(app, { backend: new GoogleAIBackend() });
  return getGenerativeModel(ai, { model: nombre, generationConfig: { responseMimeType: 'application/json', temperature: 0 } });
};
const aB64 = file => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1]); r.onerror = rej; r.readAsDataURL(file); });
const parseJSON = t => {
  const s = String(t || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '');
  try { return JSON.parse(s); } catch (e) {
    const m = s.match(/[\[{][\s\S]*[\]}]/); if (m) { try { return JSON.parse(m[0]); } catch (e2) {} }
    throw { code: 'invalid_json' };
  }
};
async function generar(partes) {
  let ultimo;
  for (const nombre of GEMINI_MODELOS) {
    try { const r = await modelo(nombre).generateContent(partes); return parseJSON(r.response.text()); }
    catch (e) { if (e?.code === 'invalid_json') throw e; ultimo = e; console.warn('Gemini', nombre, e); if (!/404|not found|not_found/i.test(String(e?.message || e))) break; }
  }
  const msg = String(ultimo?.message || ultimo || 'Gemini no disponible');
  if (/not been used|disabled|SERVICE_DISABLED|api-not-enabled|PERMISSION_DENIED|403|API_KEY_SERVICE_BLOCKED|blocked/i.test(msg)) throw { code: 'ia_off', message: msg };
  throw { code: 'unavailable', message: msg.slice(0, 160) };
}
window.GEMINI_LEER = async (file, prompt) => generar([prompt, { inlineData: { data: await aB64(file), mimeType: file.type || 'image/jpeg' } }]);
window.GEMINI_TEXTO = async prompt => generar([prompt]);

/* ---------- interfaz de inicio de sesión ---------- */
const css = `
#fbx{position:fixed;inset:0;z-index:9999;display:grid;place-items:center;padding:16px;background:var(--bg,#f3f5fa);font-family:var(--body,system-ui,sans-serif);color:var(--ink,#111a33)}
#fbx .card{width:100%;max-width:380px;background:var(--surface,#fff);border:1px solid var(--line,#dbe0ec);border-radius:14px;padding:28px 24px;display:grid;gap:14px;box-shadow:0 10px 30px rgba(10,20,50,.08)}
#fbx h1{margin:0;font-family:var(--display,system-ui);font-weight:900;font-size:30px;color:var(--brand-text,#124092);letter-spacing:-.01em}
#fbx p{margin:0;color:var(--muted,#5b6582);font-size:14px;line-height:1.45}
#fbx label{display:grid;gap:6px;font-size:13px;font-weight:600;color:var(--muted,#5b6582)}
#fbx input[type=email],#fbx input[type=password]{font:inherit;font-size:16px;padding:11px 12px;border:1px solid var(--line,#dbe0ec);border-radius:10px;background:var(--surface2,#f7f8fc);color:var(--ink,#111a33)}
#fbx input:focus{outline:2px solid var(--brand,#124092);outline-offset:1px}
#fbx button{font:inherit;font-weight:700;font-size:15px;padding:12px;border-radius:10px;border:0;cursor:pointer;background:var(--brand,#124092);color:#fff}
#fbx button.link{background:none;color:var(--brand-text,#124092);padding:4px;font-weight:600;font-size:13px}
#fbx button:disabled{opacity:.6;cursor:default}
#fbx .err{color:var(--bad,#b3261e);font-size:13px;min-height:1em}
#fbx .ok{color:var(--good,#1f7a4d);font-size:13px}
#fbx .row{display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap}
#fbx progress{width:100%}
`;
document.head.appendChild(Object.assign(document.createElement('style'), { textContent: css }));
const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
let box = null;
function pantalla(html) {
  if (!box) { box = document.createElement('div'); box.id = 'fbx'; (document.body || document.documentElement).appendChild(box); }
  box.innerHTML = `<div class="card">${html}</div>`;
  return box;
}
function quitarPantalla() { box?.remove(); box = null; }
function avisar(msg) {
  const r = document.getElementById('toastRoot');
  if (!r) return;
  const t = Object.assign(document.createElement('div'), { className: 'toast', textContent: msg });
  r.appendChild(t); setTimeout(() => t.remove(), 5000);
}
const ERR = {
  'auth/invalid-credential': 'Correo o contraseña incorrectos.',
  'auth/invalid-email': 'El correo no es válido.',
  'auth/user-not-found': 'No existe una cuenta con ese correo.',
  'auth/wrong-password': 'Contraseña incorrecta.',
  'auth/too-many-requests': 'Demasiados intentos. Espera unos minutos.',
  'auth/email-already-in-use': 'Ya existe una cuenta con ese correo. Usa "Ingresar".',
  'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres.',
  'auth/network-request-failed': 'Sin conexión a internet.',
  'auth/operation-not-allowed': 'El acceso con correo y contraseña no está habilitado en Firebase.',
};
const msgErr = e => ERR[e?.code] || (e?.code || e?.message || 'Error');

function login(modo = 'entrar', aviso = '') {
  const crear = modo === 'crear';
  const el = pantalla(`<h1>Inquiapp</h1>
    <p>${crear ? 'Crea la cuenta con la que se usará la caja.' : 'Ingresa con tu cuenta.'}</p>
    <form id="fbf" style="display:grid;gap:12px">
      <label>Correo<input type="email" id="fbe" autocomplete="username" required></label>
      <label>Contraseña<input type="password" id="fbp" autocomplete="${crear ? 'new-password' : 'current-password'}" required minlength="6"></label>
      <div class="err" id="fberr"></div>${aviso ? `<div class="ok">${esc(aviso)}</div>` : ''}
      <button type="submit" id="fbgo">${crear ? 'Crear cuenta' : 'Ingresar'}</button>
    </form>
    <div class="row"><button class="link" id="fbalt">${crear ? 'Ya tengo cuenta' : 'Crear cuenta'}</button>${crear ? '' : '<button class="link" id="fbolv">Olvidé mi contraseña</button>'}</div>`);
  const $e = el.querySelector('#fbe'), $p = el.querySelector('#fbp'), $err = el.querySelector('#fberr'), $go = el.querySelector('#fbgo');
  $e.value = localStorage.getItem('fb_email') || '';
  ($e.value ? $p : $e).focus();
  el.querySelector('#fbalt').onclick = () => login(crear ? 'entrar' : 'crear');
  el.querySelector('#fbolv')?.addEventListener('click', async () => {
    if (!$e.value) { $err.textContent = 'Escribe tu correo primero.'; return; }
    try { await sendPasswordResetEmail(auth, $e.value.trim()); login('entrar', 'Te enviamos un correo para restablecer la contraseña.'); }
    catch (e) { $err.textContent = msgErr(e); }
  });
  el.querySelector('#fbf').onsubmit = async ev => {
    ev.preventDefault(); $err.textContent = ''; $go.disabled = true;
    try {
      try { localStorage.setItem('fb_email', $e.value.trim()); } catch (e) {}
      if (crear) { const c = await createUserWithEmailAndPassword(auth, $e.value.trim(), $p.value); await sendEmailVerification(c.user).catch(() => {}); }
      else await signInWithEmailAndPassword(auth, $e.value.trim(), $p.value);
    } catch (e) { $err.textContent = msgErr(e); $go.disabled = false; }
  };
}

function verificar(user) {
  const el = pantalla(`<h1>Verifica tu correo</h1>
    <p>Enviamos un enlace de verificación a <b>${esc(user.email)}</b>. Ábrelo y luego presiona "Ya lo verifiqué".</p>
    <div class="err" id="fberr"></div>
    <button id="fbok">Ya lo verifiqué</button>
    <div class="row"><button class="link" id="fbre">Reenviar correo</button><button class="link" id="fbout">Usar otra cuenta</button></div>`);
  el.querySelector('#fbok').onclick = async () => { await reload(user); if (user.emailVerified) { await user.getIdToken(true); iniciar(user); } else el.querySelector('#fberr').textContent = 'Aún no aparece verificado.'; };
  el.querySelector('#fbre').onclick = async () => { try { await sendEmailVerification(user); el.querySelector('#fberr').textContent = 'Correo reenviado.'; } catch (e) { el.querySelector('#fberr').textContent = msgErr(e); } };
  el.querySelector('#fbout').onclick = () => signOut(auth);
}

function sinAcceso(user) {
  const el = pantalla(`<h1>Sin acceso</h1>
    <p>La cuenta <b>${esc(user.email)}</b> no está autorizada para usar Inquiapp. Pide al administrador que la habilite.</p>
    <button id="fbout">Usar otra cuenta</button>`);
  el.querySelector('#fbout').onclick = () => signOut(auth);
}

/* ---------- importación inicial de datos (respaldo .json) ---------- */
function importar(user) {
  const el = pantalla(`<h1>Cargar datos</h1>
    <p>La base de datos está vacía. Selecciona el archivo de respaldo <b>respaldo-inquiapp-….json</b> para cargar toda la información.</p>
    <input type="file" id="fbfile" accept=".json,application/json">
    <progress id="fbprog" value="0" max="1" hidden></progress>
    <div class="err" id="fberr"></div>
    <div class="row"><button class="link" id="fbvacia">Empezar sin datos</button><button class="link" id="fbout">Cerrar sesión</button></div>`);
  el.querySelector('#fbout').onclick = () => signOut(auth);
  el.querySelector('#fbvacia').onclick = () => { if (confirm('¿Empezar con la base de datos vacía?')) entregar(user); };
  el.querySelector('#fbfile').onchange = async ev => {
    const f = ev.target.files[0]; if (!f) return;
    const $err = el.querySelector('#fberr'), $pr = el.querySelector('#fbprog');
    try {
      const j = JSON.parse(await f.text());
      const docs = Object.entries(j.docs || {});
      if (!docs.length) throw new Error('El archivo no contiene datos.');
      $pr.hidden = false; $pr.max = docs.length; $pr.value = 0;
      for (let i = 0; i < docs.length; i += 300) {
        const b = writeBatch(fs);
        docs.slice(i, i + 300).forEach(([p, d]) => b.set(doc(fs, p), d));
        await b.commit();
        $pr.value = Math.min(docs.length, i + 300);
      }
      entregar(user);
    } catch (e) { $err.textContent = 'No fue posible importar: ' + (e?.code || e?.message || e); }
  };
}

/* ---------- arranque ---------- */
let entregado = false;
function entregar(user) {
  quitarPantalla();
  if (entregado) return;
  entregado = true;
  window.__fbResolve({
    db: dbApi,
    downloads: downloadsApi,
    sample: null,
    user: { id: user.uid, email: user.email, nombre: user.displayName || user.email, can: async () => true },
  });
}
async function iniciar(user) {
  if (entregado) { quitarPantalla(); return; }
  pantalla('<h1>Inquiapp</h1><p>Conectando…</p>');
  try {
    const s = await getDoc(doc(fs, 'config/carta'));
    if (!s.exists()) { importar(user); return; }
    entregar(user);
  } catch (e) {
    if (e?.code === 'permission-denied') { sinAcceso(user); return; }
    if (e?.code === 'unavailable') { entregar(user); return; } // sin internet: usa la copia local
    pantalla(`<h1>Inquiapp</h1><p class="err">No fue posible conectar: ${esc(e?.code || e?.message)}</p><button onclick="location.reload()">Reintentar</button>`);
  }
}
onAuthStateChanged(auth, user => {
  if (!user) {
    if (entregado) { location.reload(); return; }
    login(); return;
  }
  if (!user.emailVerified) { verificar(user); return; }
  iniciar(user);
});

const enlazarSalir = () => {
  const b = document.getElementById('fbSalir');
  if (b && !b.dataset.ok) {
    b.dataset.ok = '1';
    b.addEventListener('click', () => { if (confirm('¿Cerrar la sesión de Inquiapp en este equipo?')) signOut(auth); });
  }
};
enlazarSalir();
document.addEventListener('DOMContentLoaded', enlazarSalir);
